import { Chip, Container, Grid, Skeleton, Typography } from "@mui/material";
import ContentHeader from "../../../components/layout/ContentHeader/ContentHeader";
import Section from "../../../components/layout/Section/Section";
import { useEffect, useState } from "react";
import { apiClient } from "../../../services/api_client";
import { useParams } from "react-router-dom";
import parse from 'html-react-parser';
import { formatDate } from "../../../utils/formatDate";
import NavBreadcrumbs from "../../../components/navigation/NavBreadcrumbs/NavBreadcrumbs";
import { PAGE } from "../../../constants/page";
import ArrowButton from "../../../components/ui/ArrowButton/ArrowButton";
import OtherSection from "../../../components/display/OtherSection/OtherSection";
import Hr from "../../../components/ui/Hr/Hr";
import { Helmet } from "react-helmet";

interface Props {
  children?: React.ReactElement<unknown>;
}

export default function News(props: Props) {
  /** お知らせID */
  const { id } = useParams()
  /** お知らせ */
  const [news, setNews] = useState<News>({
    id: '',
    preview: false,
    tags: [],
    date: '',
    thumbnail: {
      url: '',
      height: 0,
      width: 0
    },
    title: '',
    summary: '',
    content: '',
  })
  /** 読み込み中判定 */
  const [isLoading, setIsLoading] = useState<boolean>(true)
  /** 読み込み失敗判定 */
  const [isNotFound, setIsNotFound] = useState<boolean>(false)

  useEffect(() => {
    // お知らせ取得API
    const fetchData = async () => {
      if (id) {
        // パスパラメータが設定されている場合
        await apiClient.getNews({
          // mock: 'GetNews.json'
          pathParams: [id]
        })
          .then((res: News) => {
            setNews(res)
          })
          .catch(() => {
            setIsLoading(false)
            setIsNotFound(true)
          })
          .finally(() => {
            setIsLoading(false)
          })
      } else {
        // パスパラメータが設定されていない場合
        setIsLoading(false)
        setIsNotFound(true)
      }
    }
    fetchData()
  }, [])

  return (
    <>
      <Helmet>
        <title>
          {`${news?.title}${news?.title && ' - '}${process.env.REACT_APP_NAME}`}
        </title>
        <meta name="description" content={news.summary} />
      </Helmet>

      <Section color="dark">
        <ContentHeader title={PAGE.NEWS.NAME} subTitle={PAGE.NEWS.TEXT} />
      </Section>

      <Section color="light" padding="1rem 0">
        <Container>
          <NavBreadcrumbs />
        </Container>
      </Section>

      <Section color="light">
        <Container sx={{ marginBottom: 'auto' }}>
          {isNotFound ?
            <Grid container>
              <Grid size={12}>
                <Typography variant="body1" component="p" fontSize={'1.25rem'} >
                  お知らせが見つかりませんでした。
                </Typography>
              </Grid>
            </Grid>
            :
            <Grid container>
              <Grid size={12}>
                {isLoading ?
                  <Skeleton animation="wave" width={'5rem'} />
                  :
                  news.tags.map((t, i) => <Chip sx={{ marginRight: '0.25rem', marginBottom: '1rem' }} key={i} label={t} variant="outlined" />)
                }
              </Grid>
              <Grid size={12}>
                <Typography variant="body2" fontSize={"1rem"} color={"text.secondary"}>
                  {isLoading ?
                    <Skeleton animation="wave" width={'10rem'} />
                    :
                    formatDate(news.date)
                  }
                </Typography>
              </Grid>
              <Grid size={12} sx={{
                paddingTop: '2rem',
                paddingBottom: '1rem'
              }}>
                <Typography
                  sx={(theme) => ({
                    paddingBottom: '0.5rem',
                    borderBottom: '0.5rem solid',
                    borderColor: theme.palette.grey[500],
                    [theme.breakpoints.up('xs')]: {
                      fontSize: '1.5rem',
                    },
                    [theme.breakpoints.up('md')]: {
                      fontSize: '1.75rem',
                    },
                    [theme.breakpoints.up('lg')]: {
                      fontSize: '2rem',
                    },
                  })}
                  color={"text.primary"}
                  variant="h2"
                  fontWeight={'bold'}>
                  {isLoading ?
                    <Skeleton animation="wave" />
                    :
                    news.title
                  }
                </Typography>
              </Grid>
              <Grid size={12} sx={{
                paddingTop: '1rem',
                paddingBottom: '2rem'
              }}>
                <Typography
                  variant="body2"
                  component="div"
                  sx={{
                    color: 'text.primary',
                    '& img': {
                      maxWidth: '100%',
                      height: 'auto'
                    }
                  }}
                  fontSize={'1rem'}
                >
                  {isLoading ?
                    <>
                      <Skeleton animation="wave" width="50%" />
                      <Skeleton animation="wave" width="50%" />
                      <Skeleton animation="wave" width="50%" />
                    </>
                    :
                    parse(news.content || '')
                  }
                </Typography>
              </Grid>
            </Grid>
          }
        </Container>
      </Section >

      <Section height="100px" color="light">
        <Container>
          <Grid>
            <Grid size={12} sx={{ textAlign: 'center' }}>
              <ArrowButton label={PAGE.NEWS.NAME} href={PAGE.NEWS.PATH} isLeft />
            </Grid>
          </Grid>
        </Container >
      </Section>

      <Hr />

      <Section color='dark' height={'300px'} backgroundImage='url(/assets/images/top.jpg)' isBackgroundCover>
        <Container>
          <OtherSection isObserver={false} />
        </Container>
      </Section>
    </>
  )
}